import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';

import { shallowEqual } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../App/redux/store';
import { APIStatus } from '../../../services/axiosEnums';
import getIdFromIdIri from '../../../shared/parsers/getIdFromIdIri';
import { adStudiesActions } from '../../../store/adStudies/actions';
import { getActiveResearchState } from '../../activeResearch/activeResearchSlice';
import { modalsActions } from '../../modals/modalsSlice';
import usePiecework from '../../piecework/usePiecework';
import { adActions, getAdState } from '../adSlice';
import fetchAd from '../services/fetchAd';
import fetchAdsThunk from '../services/thunks/fetchAdsThunk';

interface ILoadAdsProps {
  paginationProp: AdPaginationDatas;
  force?: boolean;
}
interface IUseAds {
  noLoadAds?: boolean;
  noResetAds?: boolean;
}

function useAds({ noLoadAds, noResetAds = false }: IUseAds) {
  const { folderId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [similaryAd, setSimilaryAd] = useState<Ad | null>(null);
  const { marketResearchIdIri } = usePiecework({});
  const { currentActiveResearch } = useAppSelector(
    getActiveResearchState,
    shallowEqual,
  );
  const { ads, activeAd, selectedAds, similarAdPlots, pagination } = useAppSelector(
    getAdState,
    shallowEqual,
  );
  const dispatch = useAppDispatch();

  // get ads source : piecework or ra result
  const baseUrl: PageType | null = (pathname?.split('/')?.[1] as PageType) ?? null;

  const loadActiveAd = (ad: Ad) => {
    if (baseUrl !== 'details-dossier') {
      dispatch(modalsActions.adDetails(ad.idIri));
    } else {
      if (folderId && ad.adStudy?.idIri) {
        dispatch(adStudiesActions.activeAdStudy.set(ad));
        navigate(
          `/creation-annonce-dossier/${folderId}/${getIdFromIdIri(
            ad.adStudy.idIri,
          )}`,
        );
      }
    }
  };

  const createStudyFromAd = () => {
    dispatch(
      modalsActions.chooseFolder({ action: 'CREATE', currentFolderIdIri: null }),
    );
  };
  const deleteAds = (value: boolean) => {
    dispatch(modalsActions.deleteAds(value));
    if (!value) {
      dispatch(adActions.selectedAdsReset());
    }
  };
  const loadAds = ({ paginationProp, force }: ILoadAdsProps) => {
    if (!isEqual(paginationProp, pagination) || force || ads.result === null) {
      dispatch(
        fetchAdsThunk({
          page: paginationProp.page,
          itemsPerPage: paginationProp.itemsPerPage,
          sort: paginationProp.sort,
          pageType: baseUrl,
          totalAds: paginationProp.totalAds,
          folderIdIri: folderId ? `folders/${folderId}` : null,
        }),
      );
    }
  };
  const addItemToSelectedAd = (ad: Ad) => {
    dispatch(adActions.selectedAdsAdd(ad));
  };
  const handlePaginationChange = (pagination: AdPaginationDatas) =>
    loadAds({ paginationProp: pagination });

  useEffect(() => {
    return () => {
      if (!noResetAds) {
        dispatch(adActions.reset());
      }
    };
  }, []);

  useEffect(() => {
    if (baseUrl === 'ad') {
      fetchAd({ idIri: pathname }).then(
        (res) => {
          setSimilaryAd(res);
        },
        (err) => {},
      );
    }
  }, [pathname]);

  useEffect(() => {
    if (
      !ads.result &&
      !noLoadAds &&
      ads.apiStatus !== APIStatus.PENDING &&
      ((baseUrl === 'recherche-active-resultat' && currentActiveResearch.result) ||
        (baseUrl === 'pige-classique' && marketResearchIdIri) ||
        baseUrl === 'details-dossier')
    ) {
      loadAds({ paginationProp: pagination, force: true });
    }
  }, [baseUrl, marketResearchIdIri, currentActiveResearch.result, ads.result]);

  return {
    ads,
    activeAd,
    loadActiveAd,
    selectedAds,
    similarAdPlots,
    loadAds,
    adPagination: pagination,
    handlePaginationChange,
    createStudyFromAd,
    deleteAds,
    addItemToSelectedAd,
    pageOrigin: baseUrl,
    similaryAd,
  };
}

export default useAds;
